/**
 * The variance table: expected against counted, per line, with the reviewer's
 * Accept / Recount / Reject decision.
 *
 * Three modes:
 *   `plain`  history — figures only.
 *   `flags`  read-only, but flagged rows still carry their marker (the waiting
 *            and owner-side views).
 *   `lines`  the working mode: per-line decisions.
 *
 * `pickable` adds the leading checkbox column used while the messages panel is
 * open, so a reply can point at specific rows.
 *
 * Virtualization kicks in above 200 rows (DESIGN.md §14) using the spacer-row
 * shape already proven in `features/reporting/components/ConsumptionTab.tsx`:
 * real `<tr>`s in normal table flow, bracketed by two zero-content rows, so
 * column widths stay honest. That rules out `scrollIntoView` for the
 * jump-to-row affordance (the target row may not be mounted), hence the
 * imperative handle below, which routes through the virtualizer when it is
 * active and falls back to the DOM when it is not.
 *
 * `border-separate` is deliberate: with the default collapsed border model a
 * `box-shadow` on a `<tr>` is not painted, and the 1100ms focus glow
 * (`animate-unlock-glow`, DESIGN.md §5) is exactly that. Cell borders move to
 * the `<td>`s to compensate.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowLeftRight, Search, TrendingDown } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { StocktakeLineDecision, StocktakeProposalLineRow } from '@threadheads/shared';
import { dropPercent } from '../lib/flag-cards';
import { formatQty, formatSigned } from '../lib/format';
import { FlagChip } from './StocktakeStatusPill';

const VIRTUALIZE_ABOVE = 200;
const ESTIMATED_ROW_HEIGHT = 56;

export type VarianceTableMode = 'plain' | 'flags' | 'lines';

export interface VarianceTableHandle {
  /** Scroll a SKU's row to the middle of the table viewport. */
  jumpToSku: (sku: string) => void;
}

interface VarianceTableProps {
  lines: StocktakeProposalLineRow[];
  mode: VarianceTableMode;
  /** Total before the search filter, for the "showing N of M" line. */
  totalCount: number;
  /** Changes when the subject changes (proposal id + search), resetting scroll. */
  resetKey?: string;
  pickable?: boolean;
  selectedSkus?: ReadonlySet<string>;
  onToggleSku?: (sku: string) => void;
  highlightedSkus?: ReadonlySet<string>;
  focusedSku?: string | null;
  /**
   * Bumped on every jump, including a jump to the SKU that is already focused.
   * It keys the focused row so React remounts it and the glow replays; without
   * it, re-clicking the same reference entry scrolls but never re-fires.
   */
  focusNonce?: number;
  onDecision?: (lineId: string, decision: StocktakeLineDecision) => void;
  decisionsDisabled?: boolean;
  /** SKU/title filter, owned by the table. Omit both to hide the input. */
  filterValue?: string;
  onFilterChange?: (value: string) => void;
  className?: string;
}

const DECISIONS: StocktakeLineDecision[] = ['accept', 'recount', 'reject'];
const DECISION_LABEL_KEY: Record<StocktakeLineDecision, string> = {
  accept: 'stocktakes.decision.accept',
  recount: 'stocktakes.decision.recount',
  reject: 'stocktakes.decision.reject',
};
const DECISION_ACTIVE_CLASS: Record<StocktakeLineDecision, string> = {
  accept: 'bg-emerald-100 text-emerald-800',
  recount: 'bg-amber-100 text-amber-800',
  reject: 'bg-orange-100 text-orange-700',
};

export const VarianceTable = forwardRef<VarianceTableHandle, VarianceTableProps>(
  function VarianceTable(
    {
      lines,
      mode,
      totalCount,
      resetKey,
      pickable = false,
      selectedSkus,
      onToggleSku,
      highlightedSkus,
      focusedSku,
      focusNonce = 0,
      onDecision,
      decisionsDisabled = false,
      filterValue,
      onFilterChange,
      className,
    },
    ref,
  ) {
    const { t } = useTranslation('inventory');
    const scrollRef = useRef<HTMLDivElement>(null);
    const virtualize = lines.length > VIRTUALIZE_ABOVE;

    const rowVirtualizer = useVirtualizer({
      count: virtualize ? lines.length : 0,
      getScrollElement: () => scrollRef.current,
      estimateSize: () => ESTIMATED_ROW_HEIGHT,
      overscan: 12,
      getItemKey: (index) => lines[index]?.id ?? index,
    });

    const indexBySku = useMemo(() => {
      const map = new Map<string, number>();
      lines.forEach((line, index) => {
        if (line.sku) map.set(line.sku, index);
      });
      return map;
    }, [lines]);

    const jumpToSku = useCallback(
      (sku: string) => {
        const index = indexBySku.get(sku);
        if (index == null) return;
        const reduceMotion =
          typeof window !== 'undefined' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (virtualize) {
          rowVirtualizer.scrollToIndex(index, {
            align: 'center',
            behavior: reduceMotion ? 'auto' : 'smooth',
          });
          return;
        }
        const row = scrollRef.current?.querySelector<HTMLTableRowElement>(
          `[data-sku-row="${CSS.escape(sku)}"]`,
        );
        row?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      },
      [indexBySku, rowVirtualizer, virtualize],
    );

    useImperativeHandle(ref, () => ({ jumpToSku }), [jumpToSku]);

    // A shrinking row set must not strand the viewport past the end — but only
    // reset on a deliberate change of subject (proposal / search), never on a
    // refetch, which would yank the list back to the top mid-decision.
    useEffect(() => {
      scrollRef.current?.scrollTo({ top: 0 });
    }, [resetKey]);

    const virtualRows = virtualize ? rowVirtualizer.getVirtualItems() : [];
    const totalSize = rowVirtualizer.getTotalSize();
    const paddingTop = virtualRows.length > 0 ? virtualRows[0]?.start ?? 0 : 0;
    const paddingBottom =
      virtualRows.length > 0
        ? totalSize - (virtualRows[virtualRows.length - 1]?.end ?? totalSize)
        : 0;

    const columnCount = 4 + (pickable ? 1 : 0) + (mode === 'lines' ? 1 : 0);

    const renderRow = (line: StocktakeProposalLineRow) => {
      const sku = line.sku ?? '';
      const flagged = mode !== 'plain' && line.flag != null;
      const picked = pickable && !!sku && selectedSkus?.has(sku);
      const highlighted = !!sku && highlightedSkus?.has(sku);
      const focused = !!sku && focusedSku === sku;
      const delta = line.countedQty == null ? null : line.countedQty - line.expectedQty;
      const decisionActive = mode === 'lines' && line.decision !== 'accept';

      return (
        <tr
          // The nonce rides the key so a repeat jump to the SAME sku remounts
          // the row and replays the glow.
          key={focused ? `${line.id}:${focusNonce}` : line.id}
          data-sku-row={sku || undefined}
          className={cn(
            'align-middle',
            !highlighted && !focused && !picked && 'hover:bg-muted/40',
            decisionActive && line.decision === 'recount' && 'bg-amber-50',
            decisionActive && line.decision === 'reject' && 'opacity-60',
            picked && 'bg-threadheads-purple/[0.06]',
            // The whole referenced set arrives at once when a reference is
            // expanded, so it fades in rather than snapping.
            highlighted && 'bg-threadheads-purple/[0.12]',
            highlighted && !focused && 'motion-safe:animate-fade-in',
            focused && 'bg-threadheads-purple/[0.18] motion-safe:animate-unlock-glow',
          )}
        >
          {pickable ? (
            <td className="w-9 border-b border-border px-3 py-2.5">
              {sku ? (
                <Checkbox
                  checked={!!picked}
                  onCheckedChange={() => onToggleSku?.(sku)}
                  aria-label={t('stocktakes.messages.attachAria', { sku })}
                />
              ) : null}
            </td>
          ) : null}
          <td
            className={cn(
              'border-b border-border px-3 py-2.5',
              flagged && 'shadow-[inset_3px_0_0_0_#F59E0B]',
            )}
          >
            <div
              className={cn(
                'font-mono text-xs font-medium',
                line.decision === 'reject' && mode === 'lines' && 'line-through',
              )}
            >
              {sku || '-'}
            </div>
            {line.title ? (
              <div className="mt-0.5 text-[11px] text-muted-foreground">{line.title}</div>
            ) : null}
            {flagged ? (
              <div className="mt-1.5">
                {line.flag === 'mirror' ? (
                  <FlagChip>
                    <ArrowLeftRight className="h-3 w-3" aria-hidden="true" />
                    {t('stocktakes.flag.mirrorChip')}
                  </FlagChip>
                ) : (
                  <FlagChip>
                    <TrendingDown className="h-3 w-3" aria-hidden="true" />
                    {t('stocktakes.flag.dropChip', { pct: dropPercent(line) })}
                  </FlagChip>
                )}
              </div>
            ) : null}
          </td>
          <td className="border-b border-border px-3 py-2.5 text-right tabular-nums">
            {formatQty(line.expectedQty)}
          </td>
          <td className="border-b border-border px-3 py-2.5 text-right tabular-nums">
            {line.countedQty == null ? (
              <span className="text-muted-foreground">{t('stocktakes.table.notCounted')}</span>
            ) : (
              formatQty(line.countedQty)
            )}
          </td>
          <td
            className={cn(
              'border-b border-border px-3 py-2.5 text-right font-semibold tabular-nums',
              delta == null || delta === 0
                ? 'font-normal text-muted-foreground'
                : delta > 0
                  ? 'text-emerald-700'
                  : 'text-destructive',
            )}
          >
            {delta == null ? '-' : formatSigned(delta)}
          </td>
          {mode === 'lines' ? (
            <td className="border-b border-border px-3 py-2.5 text-right">
              <span
                className="inline-flex overflow-hidden rounded-md border"
                role="group"
                // Never the '—' glyph here: a screen reader announces it as
                // "em dash". The empty-cell glyph is for the table, not speech.
                aria-label={t('stocktakes.decision.groupAria', {
                  sku: sku || t('stocktakes.table.unknownSku'),
                })}
              >
                {DECISIONS.map((decision) => (
                  <button
                    key={decision}
                    type="button"
                    disabled={decisionsDisabled}
                    aria-pressed={line.decision === decision}
                    onClick={() => onDecision?.(line.id, decision)}
                    className={cn(
                      'border-r px-2 py-1 text-[10.5px] font-semibold last:border-r-0',
                      'transition-colors duration-200 motion-reduce:transition-none',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      line.decision === decision
                        ? DECISION_ACTIVE_CLASS[decision]
                        : 'bg-background text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {t(DECISION_LABEL_KEY[decision])}
                  </button>
                ))}
              </span>
            </td>
          ) : null}
        </tr>
      );
    };

    return (
      // The table owns the ONE scroller in the detail pane: the summary above
      // it and the actions below it are pinned by the pane's flex column, so
      // the reviewer never scrolls the totals or the approve button away.
      <div className={cn('flex min-h-0 flex-1 flex-col', className)}>
        {/* SKU search lives WITH the rows it filters, not in the page action
            bar (that one narrows the session list). Above the scroller, not
            inside it, so it cannot scroll away from the rows. */}
        {onFilterChange ? (
          <div className="mb-2 flex shrink-0 justify-end">
            <div className="relative w-56">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                value={filterValue ?? ''}
                onChange={(event) => onFilterChange(event.target.value)}
                placeholder={t('stocktakes.table.filterPlaceholder')}
                aria-label={t('stocktakes.table.filterAria')}
                className="h-7 pl-8 text-[11.5px]"
              />
            </div>
          </div>
        ) : null}
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 overflow-auto rounded-md border bg-card"
        >
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                {pickable ? (
                  <th className="sticky top-0 z-10 w-9 border-b border-border bg-muted px-3 py-2">
                    <span className="sr-only">{t('stocktakes.messages.attachColumn')}</span>
                  </th>
                ) : null}
                <th className="sticky top-0 z-10 border-b border-border bg-muted px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('stocktakes.table.sku')}
                </th>
                <th className="sticky top-0 z-10 border-b border-border bg-muted px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('stocktakes.table.expected')}
                </th>
                <th className="sticky top-0 z-10 border-b border-border bg-muted px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('stocktakes.table.counted')}
                </th>
                <th className="sticky top-0 z-10 border-b border-border bg-muted px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('stocktakes.table.change')}
                </th>
                {mode === 'lines' ? (
                  <th className="sticky top-0 z-10 border-b border-border bg-muted px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('stocktakes.table.decision')}
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-3 py-10 text-center text-sm text-muted-foreground"
                  >
                    {filterValue ? (
                      <>
                        {t('stocktakes.table.noMatchesFor', { query: filterValue })}{' '}
                        <button
                          type="button"
                          onClick={() => onFilterChange?.('')}
                          className="rounded-sm font-semibold text-threadheads-purple underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {t('stocktakes.table.clearFilter')}
                        </button>
                      </>
                    ) : (
                      t('stocktakes.table.noMatches')
                    )}
                  </td>
                </tr>
              ) : virtualize ? (
                <>
                  {paddingTop > 0 ? (
                    <tr aria-hidden="true">
                      <td colSpan={columnCount} style={{ height: paddingTop }} />
                    </tr>
                  ) : null}
                  {virtualRows.map((virtualRow) => {
                    const line = lines[virtualRow.index];
                    return line ? renderRow(line) : null;
                  })}
                  {paddingBottom > 0 ? (
                    <tr aria-hidden="true">
                      <td colSpan={columnCount} style={{ height: paddingBottom }} />
                    </tr>
                  ) : null}
                </>
              ) : (
                lines.map(renderRow)
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[11.5px] text-muted-foreground">
          <Trans
            i18nKey="stocktakes.table.blankNote"
            ns="inventory"
            components={{ b: <b className="font-semibold text-foreground" /> }}
          />{' '}
          {lines.length < totalCount
            ? t('stocktakes.table.showingFiltered', {
                shown: lines.length,
                total: totalCount,
              })
            : null}
        </p>
      </div>
    );
  },
);
